import React from 'react';
import ProfileShell from '../../components/forms/ProfileShell';
import { SectionHeader, Field, Input, Select, RadioGroup, Toggle, TextArea, inputCls } from '../../components/forms/FormPrimitives';

const blankDriver = () => ({ full_name: '', dob: '', license_number: '', currently_insured: '' });
const blankVehicle = () => ({ vin: '', year: '', make: '', model: '', use: '', annual_mileage: '', lienholder: '' });

const VEHICLE_USE_OPTIONS = ['Commute to work', 'Pleasure', 'Business', 'Farm'];
const COVERAGE_TYPE = ['Full coverage', 'Liability only', 'Not sure yet'];

const AutoProfile = () => {
  const sections = [
    {
      title: 'Driver Information',
      render: (data, update) => {
        const drivers = data.drivers || [blankDriver()];
        const addDriver = () => update({ drivers: [...drivers, blankDriver()] });
        const removeDriver = (i) => update({ drivers: drivers.filter((_, idx) => idx !== i) });
        const setDriver = (i, patch) =>
          update({ drivers: drivers.map((d, idx) => (idx === i ? { ...d, ...patch } : d)) });
        return (
          <div>
            <SectionHeader index={1} title="Driver Information" subtitle="Add each driver who will be on the policy." />
            {drivers.map((d, i) => (
              <div key={i} className="border border-gray-200 rounded-xl p-4 mb-4 bg-[#f6f5f1]" data-testid={`driver-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold" style={{ color: '#01325B' }}>Driver {i + 1}</div>
                  {drivers.length > 1 && (
                    <button type="button" onClick={() => removeDriver(i)} className="text-xs text-red-600 underline">Remove</button>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full Name" testid={`driver-${i}-name`}>
                    <Input value={d.full_name} onChange={(v) => setDriver(i, { full_name: v })} placeholder="First and Last" />
                  </Field>
                  <Field label="Date of Birth" testid={`driver-${i}-dob`}>
                    <Input type="date" value={d.dob} onChange={(v) => setDriver(i, { dob: v })} />
                  </Field>
                  <Field label="Driver License Number" testid={`driver-${i}-license`}>
                    <Input value={d.license_number} onChange={(v) => setDriver(i, { license_number: v })} />
                  </Field>
                  <Field label="Currently insured?" testid={`driver-${i}-insured`}>
                    <Select
                      value={d.currently_insured}
                      onChange={(v) => setDriver(i, { currently_insured: v })}
                      options={['Yes', 'No']}
                    />
                  </Field>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addDriver}
              className="text-sm font-semibold text-[#E07A2C] underline"
              data-testid="add-driver-btn"
            >
              + Add another driver
            </button>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <Field label="Current Address" testid="driver-address">
                <Input value={data.current_address} onChange={(v) => update({ current_address: v })} placeholder="123 Main St, Dallas, TX" />
              </Field>
              <Field label="Current Insurance Carrier" testid="current-carrier">
                <Input value={data.current_carrier} onChange={(v) => update({ current_carrier: v })} placeholder="e.g. State Farm" />
              </Field>
              <Field label="Current Coverage Limits" testid="current-limits">
                <Input value={data.current_limits} onChange={(v) => update({ current_limits: v })} placeholder="e.g. 100/300/100" />
              </Field>
              <Field label="Tickets/violations in past 5 years?" testid="violations">
                <Select value={data.violations_5y} onChange={(v) => update({ violations_5y: v })} options={['None', '1', '2', '3+']} />
              </Field>
              <Field label="Accidents/claims in past 5 years?" testid="claims">
                <Select value={data.claims_5y} onChange={(v) => update({ claims_5y: v })} options={['None', '1', '2', '3+']} />
              </Field>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Vehicle Information',
      render: (data, update) => {
        const vehicles = data.vehicles || [blankVehicle()];
        const addV = () => update({ vehicles: [...vehicles, blankVehicle()] });
        const removeV = (i) => update({ vehicles: vehicles.filter((_, idx) => idx !== i) });
        const setV = (i, patch) =>
          update({ vehicles: vehicles.map((v, idx) => (idx === i ? { ...v, ...patch } : v)) });
        return (
          <div>
            <SectionHeader index={2} title="Vehicle Information" subtitle="Add each vehicle on the policy." />
            {vehicles.map((v, i) => (
              <div key={i} className="border border-gray-200 rounded-xl p-4 mb-4 bg-[#f6f5f1]" data-testid={`vehicle-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold" style={{ color: '#01325B' }}>Vehicle {i + 1}</div>
                  {vehicles.length > 1 && (
                    <button type="button" onClick={() => removeV(i)} className="text-xs text-red-600 underline">Remove</button>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="VIN" testid={`vehicle-${i}-vin`}>
                    <Input value={v.vin} onChange={(val) => setV(i, { vin: val })} placeholder="17-character VIN" />
                  </Field>
                  <Field label="Year" testid={`vehicle-${i}-year`}>
                    <Input value={v.year} onChange={(val) => setV(i, { year: val })} placeholder="2023" />
                  </Field>
                  <Field label="Make" testid={`vehicle-${i}-make`}>
                    <Input value={v.make} onChange={(val) => setV(i, { make: val })} placeholder="Toyota" />
                  </Field>
                  <Field label="Model" testid={`vehicle-${i}-model`}>
                    <Input value={v.model} onChange={(val) => setV(i, { model: val })} placeholder="RAV4" />
                  </Field>
                  <Field label="Vehicle Use" testid={`vehicle-${i}-use`}>
                    <Select value={v.use} onChange={(val) => setV(i, { use: val })} options={VEHICLE_USE_OPTIONS} />
                  </Field>
                  <Field label="Annual Mileage" testid={`vehicle-${i}-mileage`}>
                    <Input value={v.annual_mileage} onChange={(val) => setV(i, { annual_mileage: val })} placeholder="12000" />
                  </Field>
                  <Field label="Lienholder (if any)" testid={`vehicle-${i}-lien`}>
                    <Input value={v.lienholder} onChange={(val) => setV(i, { lienholder: val })} placeholder="Bank name or N/A" />
                  </Field>
                </div>
              </div>
            ))}
            <button type="button" onClick={addV} className="text-sm font-semibold text-[#E07A2C] underline" data-testid="add-vehicle-btn">
              + Add another vehicle
            </button>
          </div>
        );
      },
    },
    {
      title: 'Coverage Preferences',
      render: (data, update) => (
        <div>
          <SectionHeader index={3} title="Coverage Preferences" subtitle="Help us match coverage that fits your needs." />
          <div className="space-y-3 mb-4">
            <Toggle
              value={data.rental_reimbursement}
              onChange={(v) => update({ rental_reimbursement: v })}
              label="I'd like rental reimbursement"
              testid="pref-rental"
            />
            <Toggle
              value={data.roadside}
              onChange={(v) => update({ roadside: v })}
              label="I'd like roadside assistance"
              testid="pref-roadside"
            />
            <Toggle
              value={data.bundle_home_or_renters}
              onChange={(v) => update({ bundle_home_or_renters: v })}
              label="Interested in bundling home or renters insurance"
              testid="pref-bundle"
            />
          </div>
          <Field label="Full coverage or liability only?" testid="pref-coverage-type">
            <RadioGroup
              name="coverage_choice"
              value={data.coverage_choice}
              onChange={(v) => update({ coverage_choice: v })}
              options={COVERAGE_TYPE}
            />
          </Field>
        </div>
      ),
    },
    {
      title: 'Young Driver Discounts',
      render: (data, update) => (
        <div>
          <SectionHeader index={4} title="Young Driver Discounts" subtitle="Skip if not applicable — these can unlock discounts." />
          <div className="grid sm:grid-cols-1 gap-4">
            <Field label="Does your child currently attend school?" testid="young-school">
              <Select value={data.child_in_school} onChange={(v) => update({ child_in_school: v })} options={['Yes', 'No', 'Not applicable']} />
            </Field>
            <Field label="Do they maintain a GPA of 3.0 or higher?" testid="young-gpa">
              <Select value={data.gpa_3_plus} onChange={(v) => update({ gpa_3_plus: v })} options={['Yes', 'No', 'Not applicable']} />
            </Field>
            <Field label="If away at college, is the vehicle with them or kept at home?" testid="young-vehicle-loc">
              <Select
                value={data.college_vehicle_location}
                onChange={(v) => update({ college_vehicle_location: v })}
                options={['With them at college', 'Kept at home', 'Not applicable']}
              />
            </Field>
          </div>
          <Field label="Anything else we should know?" testid="auto-extra-notes">
            <TextArea value={data.profile_notes} onChange={(v) => update({ profile_notes: v })} placeholder="Optional notes about your situation" />
          </Field>
        </div>
      ),
    },
  ];

  return (
    <ProfileShell
      coverageType="auto"
      sections={sections}
      personalTouchMessage="Let's see if we can find savings and coverage options for your vehicle. Complete what you can — we'll guide you from there."
    />
  );
};

export default AutoProfile;
